import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, AlertCircle, Gift, ArrowRight, ArrowLeft, Sparkles,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ??
  `${window.location.protocol}//${window.location.hostname}:8001`;

const REWARD = 100;

const Q1_OPTIONS = [
  { value: 'youtube_twitch',    label: 'YouTube / Twitch' },
  { value: 'reddit_forums',     label: 'Reddit / foros' },
  { value: 'discord',           label: 'Discord (otro server / amigo)' },
  { value: 'tiktok_instagram',  label: 'TikTok / Instagram' },
  { value: 'search_engine',     label: 'Buscador (Google)' },
  { value: 'top_lists',         label: 'Top lists (Gtop100, Xtremetop…)' },
  { value: 'word_of_mouth',     label: 'Boca a boca' },
];

const Q2_OPTIONS = [
  { value: 'lt_5',  label: 'Menos de 5h' },
  { value: '5_15',  label: '5 – 15h' },
  { value: '15_30', label: '15 – 30h' },
  { value: 'gt_30', label: 'Más de 30h' },
];

const Q3_OPTIONS = [
  { value: 'pve_raids',           label: 'PvE raids' },
  { value: 'dungeons_mythic',     label: 'Mazmorras Heroicas / Mítica+' },
  { value: 'pvp',                 label: 'PvP (arenas, BGs, world)' },
  { value: 'leveling',            label: 'Leveling / leveo lento' },
  { value: 'professions_economy', label: 'Profesiones / economía' },
  { value: 'roleplay_social',     label: 'Roleplay / social' },
  { value: 'achievements',        label: 'Achievements / coleccionismo' },
  { value: 'playerbots',          label: 'Playerbots' },
];

const Q4_OPTIONS = [
  { value: 'playerbots',        label: 'Los playerbots (jugar sin depender de otros)' },
  { value: 'community',         label: 'La comunidad y el ambiente del Discord' },
  { value: 'rates_progression', label: 'Los rates y la progresión' },
  { value: 'custom_content',    label: 'El contenido custom (módulos, eventos)' },
  { value: 'stability',         label: 'La estabilidad y el cuidado técnico' },
  { value: 'free_no_p2w',       label: 'Gratis y sin pay-to-win' },
  { value: 'friend_streamer',   label: 'Un amigo o streamer me lo recomendó' },
  { value: 'quality_of_life',   label: 'Las mejoras de Calidad de Vida' },
];

function StepDots({ step, total }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width:        i + 1 === step ? 22 : 8,
            height:       8,
            borderRadius: 4,
            background:   i + 1 <= step ? '#22d3ee' : 'rgba(34, 211, 238, 0.18)',
            transition:   'all .25s ease',
          }}
        />
      ))}
    </div>
  );
}

function SingleSelect({ options, value, onChange, testid }) {
  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid={testid}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={optionBtnStyle(active)}
          >
            <span>{opt.label}</span>
            {active && <CheckCircle size={16} style={{ color: '#22d3ee' }} />}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect({ options, value, onChange, testid, max = 3 }) {
  const toggle = (opt) => {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else if (value.length < max) {
      onChange([...value, opt]);
    }
  };
  return (
    <div data-testid={testid}>
      <p style={hintStyle}>Puedes elegir hasta {max} {max === 1 ? 'opción' : 'opciones'}.</p>
      <div style={{ display: 'grid', gap: 8 }}>
        {options.map((opt) => {
          const active   = value.includes(opt.value);
          const disabled = !active && value.length >= max;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              disabled={disabled}
              style={{
                ...optionBtnStyle(active),
                opacity: disabled ? 0.45 : 1,
                cursor:  disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <span>{opt.label}</span>
              {active && <CheckCircle size={16} style={{ color: '#22d3ee' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const optionBtnStyle = (active) => ({
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  width:          '100%',
  textAlign:      'left',
  padding:        '12px 14px',
  borderRadius:   10,
  border:         `1px solid ${active ? '#22d3ee' : 'rgba(34, 211, 238, 0.2)'}`,
  background:     active ? 'rgba(34, 211, 238, 0.10)' : 'rgba(11, 17, 32, 0.6)',
  color:          '#e0f7ff',
  fontSize:       14,
  cursor:         'pointer',
  transition:     'all .15s ease',
});

const hintStyle = {
  color:      'rgba(224, 247, 255, 0.55)',
  fontSize:   12,
  margin:     '0 0 10px',
};

const questionStyle = {
  fontFamily:  'inherit',
  fontSize:    22,
  fontWeight:  700,
  color:       '#e0f7ff',
  letterSpacing: 0.3,
  margin:      '0 0 14px',
  lineHeight:  1.25,
  textAlign:   'center',
};

export default function EncuestaPage() {
  const navigate          = useNavigate();
  const [searchParams]    = useSearchParams();
  const welcome           = searchParams.get('welcome') === '1';

  const [authStatus, setAuthStatus] = useState('loading'); // loading | ok | redirect
  const [pageState, setPageState]   = useState('form');    // form | success | already
  const [step, setStep]             = useState(1);
  const [q1, setQ1]                 = useState([]);
  const [q2, setQ2]                 = useState('');
  const [q3, setQ3]                 = useState([]);
  const [q4, setQ4]                 = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [rewardGranted, setRewardGranted] = useState(false);

  // Comprueba sesión + si ya envió la encuesta.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        const { data } = await axios.get(`${BACKEND_URL}/api/survey/status`, { withCredentials: true });
        if (cancelled) return;
        if (data.submitted) {
          setPageState('already');
          setRewardGranted(Boolean(data.reward_granted));
        }
        setAuthStatus('ok');
      } catch {
        if (!cancelled) {
          setAuthStatus('redirect');
          navigate('/login', { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const canAdvance = useMemo(() => {
    if (step === 1) return q1.length > 0;
    if (step === 2) return Boolean(q2);
    if (step === 3) return q3.length > 0;
    if (step === 4) return q4.length > 0;
    return false;
  }, [step, q1, q2, q3, q4]);

  const handleSubmit = async () => {
    if (!canAdvance) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/survey/submit`,
        { q1_source: q1, q2_hours: q2, q3_content: q3, q4_appeal: q4 },
        { withCredentials: true },
      );
      setRewardGranted(Boolean(data.reward_granted));
      setPageState('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido enviar la encuesta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (authStatus !== 'ok') {
    return (
      <PageWrapper scrollable>
        <SEOMeta title="Encuesta" noIndex />
        <Navbar />
        <main className="register-main" />
        <Footer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper scrollable>
      <SEOMeta title="Encuesta de bienvenida" noIndex />
      <Navbar />

      <main className="register-main anim-fade-up" style={{ animationDelay: '0.15s' }}>
        <div className="register-card" data-testid="encuesta-card" style={{ maxWidth: 560 }}>
          {pageState === 'success' && (
            <div data-testid="encuesta-success" style={{ textAlign: 'center' }}>
              <CheckCircle size={52} className="success-icon" style={{ margin: '0 auto 12px' }} />
              <h2 className="register-title">¡Gracias por contestar!</h2>
              <p className="register-success-text" style={{ marginTop: 8 }}>
                Tu respuesta nos ayuda a decidir en qué centrarnos.
              </p>
              <div style={rewardBoxStyle}>
                <Gift size={22} style={{ color: '#22d3ee' }} />
                {rewardGranted ? (
                  <span><strong style={{ color: '#22d3ee' }}>{REWARD} Cristales de Escarcha</strong> añadidos a tu cuenta.</span>
                ) : (
                  <span>
                    Recibirás <strong style={{ color: '#22d3ee' }}>{REWARD} Cristales de Escarcha</strong> en
                    cuanto conectes por primera vez al servidor.
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22 }}>
                <Link to="/cuenta" className="register-btn" style={{ textDecoration: 'none' }}>
                  Ir a mi cuenta
                </Link>
              </div>
            </div>
          )}

          {pageState === 'already' && (
            <div style={{ textAlign: 'center' }} data-testid="encuesta-already">
              <Sparkles size={42} style={{ color: '#22d3ee', margin: '0 auto 12px' }} />
              <h2 className="register-title">Ya has completado la encuesta</h2>
              <p className="register-subtitle" style={{ marginTop: 8 }}>
                {rewardGranted
                  ? `Los ${REWARD} Cristales de Escarcha ya están en tu cuenta. ¡Gracias!`
                  : `Tu encuesta está registrada. Conecta al servidor para recibir los ${REWARD} Cristales.`}
              </p>
              <Link to="/cuenta" className="register-btn" style={{ textDecoration: 'none', marginTop: 22, display: 'inline-block' }}>
                Volver a mi cuenta
              </Link>
            </div>
          )}

          {pageState === 'form' && (
            <>
              <div className="register-header" style={{ marginBottom: 14 }}>
                <p className="register-eyebrow" style={{ fontSize: 11 }}>ENCUESTA OPCIONAL</p>
                <h2
                  data-testid="encuesta-heading"
                  style={{
                    fontFamily: 'inherit',
                    fontSize:    18,
                    fontWeight:  500,
                    letterSpacing: 1,
                    color:       'rgba(224, 247, 255, 0.75)',
                    margin:      '4px 0 6px',
                    textTransform: 'uppercase',
                  }}
                >
                  {welcome ? 'Bienvenido a Frostbound' : 'Cuéntanos sobre ti'}
                </h2>
                <p className="register-subtitle" style={{ fontSize: 12, marginTop: 6 }}>
                  4 preguntas rápidas. Al terminar recibirás <strong style={{ color: '#22d3ee' }}>{REWARD} Cristales</strong>
                  {' '}cuando entres por primera vez al servidor.
                </p>
              </div>

              <StepDots step={step} total={4} />

              {error && (
                <div className="register-error" data-testid="encuesta-error">
                  <AlertCircle size={15} /><span>{error}</span>
                </div>
              )}

              {step === 1 && (
                <div className="form-field">
                  <h3 style={questionStyle}>¿Cómo nos has encontrado?</h3>
                  <MultiSelect options={Q1_OPTIONS} value={q1} onChange={setQ1} testid="q1" max={2} />
                </div>
              )}

              {step === 2 && (
                <div className="form-field">
                  <h3 style={questionStyle}>¿Cuántas horas a la semana sueles jugar?</h3>
                  <SingleSelect options={Q2_OPTIONS} value={q2} onChange={setQ2} testid="q2" />
                </div>
              )}

              {step === 3 && (
                <div className="form-field">
                  <h3 style={questionStyle}>¿Qué contenido te motiva más?</h3>
                  <MultiSelect options={Q3_OPTIONS} value={q3} onChange={setQ3} testid="q3" max={3} />
                </div>
              )}

              {step === 4 && (
                <div className="form-field">
                  <h3 style={questionStyle}>¿Qué te ha llamado más la atención de Frostbound?</h3>
                  <MultiSelect options={Q4_OPTIONS} value={q4} onChange={setQ4} testid="q4" max={3} />
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="register-btn"
                    style={{ flex: 1, background: 'transparent', border: '1px solid rgba(34, 211, 238, 0.3)', color: '#e0f7ff' }}
                    data-testid="encuesta-prev"
                  >
                    <ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    Atrás
                  </button>
                )}
                {step < 4 ? (
                  <button
                    type="button"
                    onClick={() => canAdvance && setStep(step + 1)}
                    disabled={!canAdvance}
                    className="register-btn"
                    style={{ flex: 1, opacity: canAdvance ? 1 : 0.5, cursor: canAdvance ? 'pointer' : 'not-allowed' }}
                    data-testid="encuesta-next"
                  >
                    Siguiente
                    <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: 'middle' }} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canAdvance || loading}
                    className={`register-btn ${loading ? 'register-btn--loading' : ''}`}
                    style={{ flex: 1, opacity: canAdvance ? 1 : 0.5 }}
                    data-testid="encuesta-submit"
                  >
                    {loading ? 'ENVIANDO...' : 'ENVIAR Y RECIBIR CRISTALES'}
                  </button>
                )}
              </div>

              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <Link to="/cuenta" className="terms-link" style={{ fontSize: 12 }} data-testid="encuesta-skip">
                  Saltar por ahora
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </PageWrapper>
  );
}

const rewardBoxStyle = {
  display:      'flex',
  alignItems:   'center',
  gap:          10,
  margin:       '18px auto 0',
  padding:      '12px 14px',
  borderRadius: 10,
  border:       '1px solid rgba(34, 211, 238, 0.35)',
  background:   'rgba(34, 211, 238, 0.08)',
  color:        '#e0f7ff',
  fontSize:     14,
  maxWidth:     420,
};
